import { toPng } from 'html-to-image'
import { useCallback, useEffect, useRef } from 'react'

const SITE_URL = 'https://emotionswheel.app'
const SHARE_TEXT = 'Find your affirmation ✨'

const PIXEL_RATIO = 2

// Pre-fetch the background and convert to a base64 data URL so that
// html-to-image can render it without making any network requests of its
// own (which silently fail on iOS Safari in PWA / share-sheet contexts).
// When the background-image CSS property is a data URL, html-to-image
// inlines it directly — no fetch, no canvas taint.
const toDataUrl = async (src: string): Promise<string | null> => {
  try {
    const resp = await fetch(src)
    if (!resp.ok) return null
    const blob = await resp.blob()
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

const BG_DATA_URL = await toDataUrl('/img/card-face-1024.webp')

function execCommandCopy(text: string): boolean {
  const el = document.createElement('textarea')
  el.value = text
  // readonly prevents the mobile keyboard from appearing on focus
  el.setAttribute('readonly', '')
  // Must stay within the visible viewport — Chrome Android refuses to focus
  // or select text in off-screen elements (top:-9999px), which causes
  // execCommand('copy') to return false. opacity:0 + pointer-events:none
  // keeps it invisible without moving it off-screen.
  el.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none'
  document.body.appendChild(el)
  el.focus({ preventScroll: true })
  el.setSelectionRange(0, text.length)
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    // not available
  }
  document.body.removeChild(el)
  return ok
}

export function useCardShare(
  cardFaceRef: React.RefObject<HTMLDivElement | null>,
  affirmationText: string | null,
) {
  const generatingRef = useRef(false)
  // Keep the last non-null text so handleCopy still works if affirmations
  // become null during a refresh race (refresh() sets affirmations→null
  // while the user is already looking at a card or picks a new one quickly).
  const affirmationTextRef = useRef(affirmationText)
  if (affirmationText !== null) {
    affirmationTextRef.current = affirmationText
  }

  // Warm up html-to-image on first render: calling toPng on a tiny element
  // triggers its internal stylesheet + font fetching and caches the results.
  // By the time the user taps Share, everything is already in memory.
  useEffect(() => {
    const dummy = document.createElement('div')
    dummy.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px'
    document.body.appendChild(dummy)
    toPng(dummy).finally(() => document.body.removeChild(dummy))
  }, [])

  const generateCardImage = useCallback(async (): Promise<string | null> => {
    const el = cardFaceRef.current
    if (!el) return null

    const rect = el.getBoundingClientRect()
    const W = rect.width * PIXEL_RATIO
    const H = rect.height * PIXEL_RATIO

    // Strip the background and the CSS class before capture so html-to-image
    // never tries to fetch the card-face-*.webp URLs from the stylesheet —
    // those fetches are slow/fail on mobile on the first call.
    // The background is composited manually below using BG_DATA_URL, which is
    // already in memory as a data URL (loaded at module init).
    const savedBg = el.style.backgroundImage
    const savedBoxShadow = el.style.boxShadow
    const savedBorderRadius = el.style.borderRadius
    const savedTransform = el.style.transform
    const hasBgClass = el.classList.contains('choice-card-face-bg')
    if (hasBgClass) el.classList.remove('choice-card-face-bg')
    el.style.backgroundImage = 'none'
    el.style.boxShadow = 'none'
    el.style.borderRadius = '0'
    el.style.transform = 'none'

    type ChildSave = { el: HTMLElement; opacity: string }
    const childSaves: ChildSave[] = []
    el.querySelectorAll<HTMLElement>('*').forEach((child) => {
      childSaves.push({ el: child, opacity: child.style.opacity })
      child.style.opacity = '1'
    })

    let textPngUrl: string | null = null
    try {
      const toPngWithTimeout = (timeoutMs: number) =>
        Promise.race([
          toPng(el, { pixelRatio: PIXEL_RATIO, width: rect.width, height: rect.height }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Image generation timed out')), timeoutMs),
          ),
        ])
      textPngUrl = await toPngWithTimeout(5_000)
    } finally {
      el.style.backgroundImage = savedBg
      el.style.boxShadow = savedBoxShadow
      el.style.borderRadius = savedBorderRadius
      el.style.transform = savedTransform
      if (hasBgClass) el.classList.add('choice-card-face-bg')
      childSaves.forEach(({ el: child, opacity }) => {
        child.style.opacity = opacity
      })
    }

    if (!textPngUrl) return null

    // Load the text layer PNG. This is a data URL so it loads instantly.
    const textImg = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = textPngUrl!
    })

    // Composite: background (from in-memory data URL) + text layer on canvas.
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')!

    // 1. Parchment fallback colour
    ctx.fillStyle = '#f8e0ac'
    ctx.fillRect(0, 0, W, H)

    // 2. Background texture — BG_DATA_URL is already in memory so the Image
    //    loads synchronously without any network request.
    if (BG_DATA_URL) {
      const bgImg = await new Promise<HTMLImageElement>((resolve) => {
        const img = new Image()
        // data: URLs fire onload synchronously in most engines, but await
        // the promise to be safe.
        img.onload = () => resolve(img)
        img.onerror = () => resolve(img) // resolve even on error; ctx.drawImage will just no-op
        img.src = BG_DATA_URL
      })
      ctx.drawImage(bgImg, 0, 0, W, H)
    }

    // 3. Text and ornaments on top
    ctx.drawImage(textImg, 0, 0, W, H)

    return canvas.toDataURL('image/jpeg', 0.95)
  }, [cardFaceRef])

  const handleShare = useCallback(async () => {
    if (generatingRef.current) return
    generatingRef.current = true
    try {
      const dataUrl = await generateCardImage()
      if (dataUrl) {
        const blob = await fetch(dataUrl).then((r) => r.blob())
        const file = new File([blob], 'affirmation.jpeg', { type: 'image/jpeg' })
        const shareData: ShareData = {
          title: 'Affirmation',
          text: `${SHARE_TEXT} ${SITE_URL}`,
          files: [file],
        }
        if (navigator.canShare?.(shareData)) {
          await navigator.share(shareData)
        } else if (navigator.share) {
          await navigator.share({ title: 'Affirmation', url: SITE_URL })
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Share failed', err)
      }
    } finally {
      generatingRef.current = false
    }
  }, [generateCardImage])

  const handleDownload = useCallback(async () => {
    if (generatingRef.current) return
    generatingRef.current = true
    try {
      const dataUrl = await generateCardImage()
      if (dataUrl) {
        const a = document.createElement('a')
        a.href = dataUrl
        a.download = 'affirmation.jpeg'
        a.click()
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        console.error('Download failed', err)
      }
    } finally {
      generatingRef.current = false
    }
  }, [generateCardImage])

  const handleCopy = useCallback(() => {
    const text = affirmationTextRef.current
    if (!text) return
    // execCommandCopy focuses a real DOM element which restores document focus
    // that Chrome Android's download bar may have stolen — window.focus() is a
    // no-op on mobile. Fall back to the async Clipboard API only when
    // execCommand is genuinely unavailable (returns false).
    if (!execCommandCopy(text) && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch((err) => console.error('Copy failed', err))
    }
  }, [])

  return { handleShare, handleDownload, handleCopy }
}
